import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Loader2 } from "lucide-react";
import Header from "../components/Header";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

const LOGIN_BG =
  "https://static.prod-images.emergentagent.com/jobs/dde13f54-cf57-488d-95a4-a46d55ab67c7/images/da5f89f17c4ac94785d930e0cdb203ee33ad36b3638f8036a68c7f042e5343f1.png";

export default function AdminLogin() {
  const { t } = useI18n();
  const { login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await login(email, password);
      nav("/admin");
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Identifiants invalides. Veuillez réessayer."
      );
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-login-page">
      <Header />
      <div className="flex-1 relative grid lg:grid-cols-2">
        <div className="absolute inset-0 lg:relative lg:col-span-1 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-90"
            style={{ backgroundImage: `url(${LOGIN_BG})` }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-primary/85" aria-hidden />
          <div className="relative h-full flex flex-col justify-between p-10 text-white">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-accent mb-4">
                / espace sécurisé
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight max-w-md">
                Portail d'administration
              </h2>
              <p className="mt-5 text-white/80 text-base max-w-sm leading-relaxed">
                Gérez le corpus juridique : importez de nouveaux textes, suivez
                les abrogations, analysez les requêtes citoyennes.
              </p>
            </div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-white/60">
              LEXIHAB · CAMEROUN · 2026
            </div>
          </div>
        </div>

        <div className="relative z-10 bg-white/0 lg:bg-white flex items-center justify-center p-6 md:p-12">
          <form
            onSubmit={submit}
            className="w-full max-w-sm bg-white lg:bg-transparent border border-primary/15 lg:border-none rounded-sm p-7 lg:p-0 shadow-xl lg:shadow-none"
            data-testid="login-form"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
              / connexion
            </div>
            <h1 className="font-display text-3xl font-semibold text-primary mb-1">
              {t("admin.loginTitle")}
            </h1>
            <p className="text-sm text-foreground/60 mb-7">
              {t("admin.loginSubtitle")}
            </p>

            <Field
              id="email"
              icon={Mail}
              label={t("admin.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              testid="login-email-input"
            />
            <Field
              id="password"
              icon={Lock}
              label={t("admin.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              testid="login-password-input"
            />

            {error && (
              <div
                className="mb-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {t("admin.signIn")}
            </button>

            <div className="mt-4 text-center">
              <a
                href="/admin/forgot-password"
                className="text-xs text-foreground/60 hover:text-primary"
                data-testid="login-forgot-link"
              >
                Mot de passe oublié ?
              </a>
            </div>

            <div className="mt-6 pt-5 border-t border-primary/10 font-mono text-[10px] text-foreground/40 tracking-wider">
              Accès réservé aux administrateurs
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ id, icon: Icon, label, testid, ...rest }) {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1.5">
        {label}
      </label>
      <div className="flex items-center gap-2 border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors">
        <Icon size={14} className="text-foreground/40" />
        <input
          id={id}
          required
          className="w-full py-2.5 outline-none text-sm bg-transparent"
          data-testid={testid}
          {...rest}
        />
      </div>
    </div>
  );
}
